import { useCallback, useEffect, useRef, useState } from 'react';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { ApiClient } from '../../services/api';
import { CallEventBus } from './events';
import { CallMachine, createCallMachine } from './state';
import { subscribeToRemotePhoneCallEvents } from './websocket';
import { TwilioDeviceController } from './twilio';
import { CallEventHandler, CallEventName, CallState, DialTarget, PracticeSessionInfo } from './types';

export type CallPipelineApi = {
	state: CallState;
	muted: boolean;
	initDevice: () => Promise<boolean>;
	dial: (to: string, params?: Omit<DialTarget, 'to'>) => Promise<boolean>;
	accept: () => void;
	reject: () => void;
	hangup: () => void;
	setMuted: (muted: boolean) => void;
	/**
	 * Send DTMF tones on the active call. Used by the in-call dialpad so reps
	 * can navigate phone trees ("press 1 for sales…"). No-ops when no call is
	 * connected.
	 */
	sendDigits: (digits: string) => void;
	/**
	 * Enter an AI voice-roleplay ("Spar") practice session. Drives the FSM into
	 * the `practice` state purely via the event bus — no Twilio Device/Call is
	 * involved. The practice transcript flows through the same coaching pipeline.
	 */
	startPractice: (info: PracticeSessionInfo) => void;
	/** End the active practice session, returning the FSM to `idle`. */
	endPractice: () => void;
	subscribe: <K extends CallEventName>(event: K, handler: CallEventHandler<K>) => () => void;
	/**
	 * Dev-only: emit an event onto the bus directly. Used by the harness page
	 * to drive FSM transitions without real Twilio traffic. Do not call from
	 * production UI.
	 */
	devEmit: <K extends CallEventName>(event: K, payload?: Parameters<CallEventBus['emit']>[1]) => void;
};

/**
 * React binding for the call pipeline. Constructs a CallEventBus +
 * TwilioDeviceController + CallMachine on mount; tears everything down on
 * unmount. Exposes call control methods and a subscribe() escape hatch for
 * components (or the AI module) that need to react to specific events rather
 * than the derived state.
 */
export function useCallPipeline(): CallPipelineApi {
	const session = useUserSession();
	const busRef = useRef<CallEventBus | null>(null);
	const controllerRef = useRef<TwilioDeviceController | null>(null);
	const machineRef = useRef<CallMachine | null>(null);

	const [state, setState] = useState<CallState>({ status: 'uninit' });
	const [muted, setMutedState] = useState(false);

	useEffect(() => {
		if (!session) return;

		const bus = new CallEventBus();
		const apiClient = new ApiClient(session);
		const controller = new TwilioDeviceController({ bus, apiClient });
		const machine = createCallMachine(bus);

		busRef.current = bus;
		controllerRef.current = controller;
		machineRef.current = machine;

		setState(machine.getState());
		const unsubState = machine.subscribe(setState);
		const unsubMute = bus.on('call:mute-changed', ({ muted: next }) => setMutedState(next));
		// Reset the muted indicator on every new call. Twilio doesn't fire a
		// `mute` event on accept/connect, so without this reset an inbound call
		// that follows a previous muted call would inherit the stale `true` UI
		// state — the mic actually isn't muted on Twilio's side, but the icon
		// shows muted and the toggle no-ops because `call.mute(false)` doesn't
		// emit a state-change event when the call is already unmuted.
		const unsubConnected = bus.on('call:connected', () => setMutedState(false));
		const unsubEnded = bus.on('call:ended', () => setMutedState(false));
		const unsubWs = subscribeToRemotePhoneCallEvents({ bus, session });

		return () => {
			unsubState();
			unsubMute();
			unsubConnected();
			unsubEnded();
			unsubWs();
			machine.dispose();
			controller.deinit();
			busRef.current = null;
			controllerRef.current = null;
			machineRef.current = null;
		};
	}, [session]);

	const initDevice = useCallback(async () => {
		return controllerRef.current?.init() ?? false;
	}, []);

	const dial = useCallback(async (to: string, params?: Omit<DialTarget, 'to'>) => {
		return controllerRef.current?.dial(to, params) ?? false;
	}, []);

	const accept = useCallback(() => controllerRef.current?.accept(), []);
	const reject = useCallback(() => controllerRef.current?.reject(), []);
	const hangup = useCallback(() => controllerRef.current?.hangup(), []);

	const setMuted = useCallback((next: boolean) => {
		controllerRef.current?.setMuted(next);
	}, []);

	const sendDigits = useCallback((digits: string) => {
		controllerRef.current?.sendDigits(digits);
	}, []);

	// Practice controls bypass the Twilio controller entirely — a Spar session is
	// a pure FSM state driven by bus events, so we emit directly on the bus.
	const startPractice = useCallback((info: PracticeSessionInfo) => {
		busRef.current?.emit('call:practice-started', info);
	}, []);

	const endPractice = useCallback(() => {
		busRef.current?.emit('call:practice-ended');
	}, []);

	const subscribe = useCallback(<K extends CallEventName>(event: K, handler: CallEventHandler<K>): (() => void) => {
		return busRef.current?.on(event, handler) ?? (() => undefined);
	}, []);

	const devEmit = useCallback(<K extends CallEventName>(event: K, payload?: any) => {
		busRef.current?.emit(event, payload);
	}, []);

	return {
		state,
		muted,
		initDevice,
		dial,
		accept,
		reject,
		hangup,
		setMuted,
		sendDigits,
		startPractice,
		endPractice,
		subscribe,
		devEmit,
	};
}
